// PREZENCE — Admin Reports & Analytics (Phase 8)
import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend } from 'recharts';
import api from '../../api/client';
import toast from 'react-hot-toast';
import { todayLocal } from '../../utils/localDate';

const getInitials = n => (n||'').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
const fmtMin = m => { const h=Math.floor((m||0)/60), mm=(m||0)%60; return h>0?`${h}h ${mm}m`:`${mm}m`; };

export default function AdminReports() {
  const [weekData, setWeekData] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState('weekly');
  const [empReport, setEmpReport] = useState(null);
  const [selEmp, setSelEmp]     = useState('');
  const [employees, setEmployees] = useState([]);
  const [selMonth, setSelMonth] = useState(new Date().getMonth()+1);
  const [selYear, setSelYear]   = useState(new Date().getFullYear());

  const fetchWeekly = useCallback(async () => {
    try {
      const res = await api.get('/reports/weekly');
      setWeekData(res.data);
    } catch { toast.error('Failed to load weekly report'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchWeekly();
    api.get('/users').then(r => setEmployees(r.data.data || [])).catch(()=>{});
  }, [fetchWeekly]);

  const fetchEmpReport = async () => {
    if (!selEmp) { toast.error('Select an employee'); return; }
    try {
      const res = await api.get(`/reports/employee/${selEmp}?month=${selMonth}&year=${selYear}`);
      setEmpReport(res.data);
    } catch { toast.error('Failed to load employee report'); }
  };

  // Build weekly chart data from attendance
  const buildWeekChart = () => {
    if (!weekData?.attendance) return [];
    const days = {};
    weekData.attendance.forEach(a => {
      const d = new Date(a.date).toLocaleDateString('en-US',{weekday:'short',day:'numeric'});
      if (!days[d]) days[d] = { day:d, present:0, absent:0, late:0 };
      // Present = actually clocked in that day, whatever the status label
      // (half_day was previously counted nowhere, leaving the chart empty).
      if (a.clock_in) {
        days[d].present++;
        if (a.is_late || a.status === 'late') days[d].late++;
      } else if (a.status === 'absent') days[d].absent++;
    });
    return Object.values(days);
  };

  const buildProdChart = () => {
    if (!weekData?.productivity) return [];
    const days = {};
    weekData.productivity.forEach(p => {
      const d = new Date(p.date).toLocaleDateString('en-IN',{weekday:'short',day:'numeric'});
      if (!days[d]) days[d] = { day:d, scores:[], avg:0 };
      days[d].scores.push(p.score);
    });
    Object.values(days).forEach(d => { d.avg = Math.round(d.scores.reduce((a,b)=>a+b,0)/d.scores.length); });
    return Object.values(days);
  };

  const exportCSV = () => {
    if (!weekData?.attendance) return;
    // Merge productivity into the weekly attendance export.
    const prodMap = {};
    (weekData.productivity || []).forEach(p => { prodMap[`${p.user_id}_${p.date}`] = p; });
    const rows = [['Name','Department','Date','Clock In','Clock Out','Hours','Status','Punctuality','Productivity %','Active (min)','Idle (min)']];
    weekData.attendance.forEach(a => {
      const p = prodMap[`${a.user_id}_${a.date}`] || {};
      rows.push([
        a.users?.full_name, a.users?.department, a.date,
        a.clock_in ? new Date(a.clock_in).toLocaleTimeString('en-US') : '',
        a.clock_out ? new Date(a.clock_out).toLocaleTimeString('en-US') : '',
        a.total_hours || '', a.status,
        a.clock_in ? ((a.is_late || a.status==='late') ? 'LATE' : 'ON TIME') : '',
        p.score ?? '', p.active_minutes ?? '', p.idle_minutes ?? '',
      ]);
    });
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type:'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `prezence-weekly-${todayLocal()}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success('Report exported!');
  };

  const exportEmpCSV = () => {
    if (!empReport?.daily?.length) { toast.error('Generate a report first'); return; }
    const emp = employees.find(e => e.id === selEmp);
    const headers = ['Employee','Date','Clock In','Clock Out','Status','Punctuality','Hours','Productivity %','Active (min)','Idle (min)','Break (min)','Meeting (min)'];
    const rows = empReport.daily.map(d => [
      emp?.full_name || '', d.date,
      d.clock_in ? new Date(d.clock_in).toLocaleTimeString('en-US') : '',
      d.clock_out ? new Date(d.clock_out).toLocaleTimeString('en-US') : '',
      d.status || '', d.clock_in ? (d.is_late ? 'LATE' : 'ON TIME') : '', d.total_hours ?? '', d.productivity ?? '',
      d.active_minutes, d.idle_minutes, d.break_minutes, d.meeting_minutes,
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type:'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${(emp?.full_name||'employee').replace(/\s+/g,'-')}-${selYear}-${String(selMonth).padStart(2,'0')}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success('Employee report exported');
  };

  const weekChart = buildWeekChart();
  const prodChart = buildProdChart();

  const TABS = ['weekly', 'employee'];

  return (
    <div style={{ animation:'fadeIn 0.4s ease' }}>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <h1 className="page-title">Reports & Analytics</h1>
          <p className="page-sub">Weekly and individual employee performance reports</p>
        </div>
        <button className="btn btn-secondary" onClick={activeTab==='employee' ? exportEmpCSV : exportCSV}>⬇ Export CSV</button>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:8, marginBottom:24, borderBottom:'1px solid rgba(56,189,248,0.08)', paddingBottom:12 }}>
        {TABS.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            background: activeTab===tab ? 'rgba(99,220,188,0.1)' : 'transparent',
            border: `1px solid ${activeTab===tab ? 'rgba(99,220,188,0.3)' : 'transparent'}`,
            color: activeTab===tab ? '#63dcbc' : '#64748b',
            borderRadius:8, padding:'8px 18px', cursor:'pointer',
            fontSize:13, fontWeight:600, fontFamily:"'DM Sans',sans-serif",
            textTransform:'capitalize', transition:'all 0.2s',
          }}>{tab} Report</button>
        ))}
      </div>

      {activeTab === 'weekly' && (
        <>
          {loading ? <div style={{ display:'flex', justifyContent:'center', padding:60 }}><div className="spinner" style={{width:28,height:28}} /></div> : (
            <>
              <div className="grid-2" style={{ marginBottom:24 }}>
                {/* Attendance chart */}
                <div className="card">
                  <div className="card-header">
                    <span className="card-title">WEEKLY ATTENDANCE</span>
                    <span style={{ fontSize:11, color:'#3ba8ff', fontFamily:"'JetBrains Mono'" }}>
                      {weekData?.period?.startDate} → {weekData?.period?.endDate}
                    </span>
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={weekChart} margin={{ left:-20 }}>
                      <XAxis dataKey="day" tick={{ fill:'#64748b', fontSize:11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill:'#64748b', fontSize:10 }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ background:"#0d1520", border:"1px solid rgba(56,189,248,0.2)", borderRadius:8, fontSize:12, color:"#e2e8f0" }} />
                      <Legend wrapperStyle={{ fontSize:11, color:'#64748b' }} />
                      <Bar dataKey="present" fill="#22c55e" radius={[3,3,0,0]} name="Present" />
                      <Bar dataKey="absent"  fill="rgba(239,68,68,0.5)" radius={[3,3,0,0]} name="Absent" />
                      <Bar dataKey="late"    fill="#f59e0b" radius={[3,3,0,0]} name="Late" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Productivity chart */}
                <div className="card">
                  <div className="card-header"><span className="card-title">TEAM PRODUCTIVITY TREND</span></div>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={prodChart} margin={{ left:-20 }}>
                      <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                      <XAxis dataKey="day" tick={{ fill:'#64748b', fontSize:11 }} axisLine={false} tickLine={false} />
                      <YAxis domain={[0,100]} tick={{ fill:'#64748b', fontSize:10 }} axisLine={false} tickLine={false} tickFormatter={v=>`${v}%`} />
                      <Tooltip contentStyle={{ background:"#0d1520", border:"1px solid rgba(56,189,248,0.2)", borderRadius:8, fontSize:12, color:"#e2e8f0" }} formatter={v=>[`${v}%`,'Avg Score']} />
                      <Line type="monotone" dataKey="avg" stroke="#63dcbc" strokeWidth={2.5} dot={{ fill:'#63dcbc', strokeWidth:0, r:4 }} name="Avg Score" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Attendance table */}
              <div className="card">
                <div className="card-header"><span className="card-title">DETAILED ATTENDANCE LOG</span></div>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Employee</th><th>Date</th><th>Clock In</th><th>Clock Out</th><th>Hours</th><th>Status</th><th>Punctuality</th></tr></thead>
                    <tbody>
                      {(weekData?.attendance || []).slice(0,30).map((a,i) => (
                        <tr key={i}>
                          <td>
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                              <div className="avatar avatar-sm">{getInitials(a.users?.full_name)}</div>
                              <div>
                                <div style={{ fontWeight:600, color:'#e2e8f0', fontSize:13 }}>{a.users?.full_name}</div>
                                <div style={{ fontSize:10, color:'#475569' }}>{a.users?.department}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ fontFamily:"'JetBrains Mono'", fontSize:12 }}>{new Date(a.date).toLocaleDateString('en-IN',{weekday:'short',day:'numeric',month:'short'})}</td>
                          <td style={{ fontFamily:"'JetBrains Mono'", fontSize:12, color:'#22c55e' }}>{a.clock_in ? new Date(a.clock_in).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}) : '—'}</td>
                          <td style={{ fontFamily:"'JetBrains Mono'", fontSize:12, color:'#ef4444' }}>{a.clock_out ? new Date(a.clock_out).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}) : '—'}</td>
                          <td style={{ fontFamily:"'JetBrains Mono'", fontSize:13, fontWeight:700, color:'#63dcbc' }}>{a.total_hours ? `${a.total_hours}h` : '—'}</td>
                          <td><span className={`badge ${a.status==='present'?'badge-green':a.status==='late'?'badge-amber':a.status==='absent'?'badge-red':'badge-gray'}`}>{a.status}</span></td>
                          <td>{a.clock_in ? (
                            (a.is_late || a.status==='late')
                              ? <span className="badge badge-red">⚠ LATE</span>
                              : <span className="badge badge-green">ON TIME</span>
                          ) : <span style={{ color:'#475569' }}>—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {activeTab === 'employee' && (
        <div>
          <div className="card" style={{ marginBottom:20, padding:'20px 24px' }}>
            <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end' }}>
              <div className="form-group" style={{ marginBottom:0, flex:'1 1 200px' }}>
                <label className="form-label">SELECT EMPLOYEE</label>
                <select className="form-select" value={selEmp} onChange={e=>setSelEmp(e.target.value)}>
                  <option value="">Choose employee...</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.full_name}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label className="form-label">MONTH</label>
                <select className="form-select" style={{ width:'auto' }} value={selMonth} onChange={e=>setSelMonth(+e.target.value)}>
                  {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m,i)=>(
                    <option key={i} value={i+1}>{m}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label className="form-label">YEAR</label>
                <select className="form-select" style={{ width:'auto' }} value={selYear} onChange={e=>setSelYear(+e.target.value)}>
                  {[2024,2025,2026].map(y=><option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <button className="btn btn-primary" onClick={fetchEmpReport}>Generate Report</button>
            </div>
          </div>

          {empReport && (
            <>
              <div className="grid-4" style={{ marginBottom:20 }}>
                {[
                  { label:'Days Worked', value: empReport.summary?.days_worked ?? 0, color:'#22c55e' },
                  { label:'Late Days',  value: empReport.summary?.late_days ?? 0, color:'#f59e0b' },
                  { label:'Leaves Taken', value: empReport.summary?.leave_days ?? 0, color:'#a78bfa' },
                  { label:'Avg Productivity', value: `${empReport.summary?.avg_productivity ?? 0}%`, color:'#63dcbc' },
                ].map((s,i) => (
                  <div key={i} className="card" style={{ padding:'18px 20px' }}>
                    <p style={{ fontSize:10, color:'#475569', letterSpacing:'0.1em', fontFamily:"'JetBrains Mono'", marginBottom:8 }}>{s.label.toUpperCase()}</p>
                    <p style={{ fontSize:28, fontWeight:800, color:s.color, fontFamily:"'JetBrains Mono'" }}>{s.value}</p>
                  </div>
                ))}
              </div>

              <div className="card" style={{ marginBottom:20 }}>
                <div className="card-header"><span className="card-title">MONTHLY PRODUCTIVITY TREND</span></div>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={(empReport.daily||[]).filter(d=>d.productivity!=null).map(p=>({ date: new Date(p.date+'T00:00:00').toLocaleDateString('en-US',{day:'numeric',month:'short'}), score: p.productivity }))} margin={{ left:-20 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="date" tick={{ fill:'#64748b', fontSize:11 }} axisLine={false} tickLine={false} />
                    <YAxis domain={[0,100]} tick={{ fill:'#64748b', fontSize:10 }} axisLine={false} tickLine={false} tickFormatter={v=>`${v}%`} />
                    <Tooltip contentStyle={{ background:"#0d1520", border:"1px solid rgba(56,189,248,0.2)", borderRadius:8, fontSize:12, color:"#e2e8f0" }} formatter={v=>[`${v}%`,'Score']} />
                    <Line type="monotone" dataKey="score" stroke="#63dcbc" strokeWidth={2.5} dot={{ fill:'#63dcbc', r:3, strokeWidth:0 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Daily breakdown table — same rich data as the employee's own report */}
              <div className="card">
                <div className="card-header">
                  <span className="card-title">DAILY BREAKDOWN</span>
                  <button className="btn btn-secondary" style={{ padding:'6px 14px', fontSize:12 }} onClick={exportEmpCSV}>⬇ Export This Report</button>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Date</th><th>In</th><th>Out</th><th>Status</th><th>Punctuality</th><th>Productivity</th><th>Active</th><th>Idle</th><th>Break</th><th>Meeting</th></tr></thead>
                    <tbody>
                      {(empReport.daily || []).map((d,i) => (
                        <tr key={i}>
                          <td style={{ fontFamily:"'JetBrains Mono'", fontSize:12 }}>{new Date(d.date+'T00:00:00').toLocaleDateString('en-US',{weekday:'short',day:'numeric',month:'short'})}</td>
                          <td style={{ fontFamily:"'JetBrains Mono'", fontSize:12, color:'#22c55e' }}>{d.clock_in ? new Date(d.clock_in).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'}) : '—'}</td>
                          <td style={{ fontFamily:"'JetBrains Mono'", fontSize:12, color:'#ef4444' }}>{d.clock_out ? new Date(d.clock_out).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'}) : '—'}</td>
                          <td><span className={`badge ${d.status==='present'?'badge-green':d.status==='late'?'badge-amber':d.status==='absent'?'badge-red':'badge-gray'}`}>{d.status||'—'}</span></td>
                          <td>{d.clock_in ? (d.is_late
                            ? <span className="badge badge-red">⚠ LATE</span>
                            : <span className="badge badge-green">ON TIME</span>) : '—'}</td>
                          <td style={{ fontFamily:"'JetBrains Mono'", fontSize:13, fontWeight:700, color: d.productivity>=80?'#22c55e':d.productivity>=60?'#f59e0b':d.productivity!=null?'#ef4444':'#475569' }}>{d.productivity!=null?`${d.productivity}%`:'—'}</td>
                          <td style={{ fontFamily:"'JetBrains Mono'", fontSize:12 }}>{fmtMin(d.active_minutes)}</td>
                          <td style={{ fontFamily:"'JetBrains Mono'", fontSize:12, color:d.idle_minutes>0?'#ef4444':'#475569' }}>{fmtMin(d.idle_minutes)}</td>
                          <td style={{ fontFamily:"'JetBrains Mono'", fontSize:12 }}>{fmtMin(d.break_minutes)}</td>
                          <td style={{ fontFamily:"'JetBrains Mono'", fontSize:12 }}>{fmtMin(d.meeting_minutes)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
